import { useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import ArticleSelect from '@components/fields/ArticleSelect';
import AsyncSelect, { Option } from '@components/fields/AsyncSelect';
import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import FormButtons from '@components/fields/FormButtons';
import GoalSelect from '@components/fields/GoalSelect';
import Input from '@components/fields/Input';
import RichTextArea from '@components/fields/RichTextArea';
import Select from '@components/fields/Select';
import TagSelect from '@components/fields/TagSelect';
import WorksheetTemplateSelect from '@components/fields/WorksheetTemplateSelect';
import Form from '@components/forms/Form';
import FormCondition from '@components/forms/FormCondition';
import useAsyncSearch from '@hooks/useAsyncSearch';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import {
  AutomationActionBlockKindEnum,
  AutomationActionBlockRequiredFieldEnum,
  AutomationActionBlockUpdateDocument,
  AutomationActionBlockUpdateMutation,
  AutomationActionBlockUpdateMutationVariables,
  AutomationFragment as AutomationType,
  AutomationUserTypeEnum,
  CarePlanTaskTemplate,
  CarePlanTaskTemplateSortByEnum,
  DurationUnitsEnum,
  GetAutomationActionBlockRulesDocument,
  GetAutomationActionBlockRulesQuery,
  GetAutomationActionBlockRulesQueryVariables,
  GetCarePlanTaskTemplatesBasicDocument,
  GetCarePlanTaskTemplatesBasicQuery,
  GetCarePlanTaskTemplatesBasicQueryVariables,
  PushNotificationActionEnum,
  TagTypeEnum,
  TaskKindEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import {
  optionsForAutomationActionBlockKinds,
  optionsForAutomationUserType,
  optionsForBoolean,
  optionsForDurationUnits,
  optionsForHour,
  optionsForPushNotificationAction,
  optionsForTaskKinds
} from '@utils/optionUtils';

type AutomationActionBlock = Extract<AutomationType['blocks'][number], { __typename: 'AutomationActionBlock' }>;

type FormData = {
  archiveTag?: string | null | undefined;
  articleId?: string | null | undefined;
  carePlanTaskTemplateId?: string | undefined;
  duration?: number | null | undefined;
  durationUnits?: DurationUnitsEnum | null | undefined;
  goalTemplateId?: string | undefined;
  kind: AutomationActionBlockKindEnum;
  messageContent?: string | null | undefined;
  pushNotificationAction?: PushNotificationActionEnum | null | undefined;
  pushNotificationContent?: string | undefined;
  tag?: string | null | undefined;
  taskClientVisible?: boolean | null | undefined;
  taskDueDays?: number | null;
  taskDueHour?: number | null;
  taskKind?: TaskKindEnum | null | undefined;
  taskTitle?: string | null | undefined;
  userType?: AutomationUserTypeEnum | undefined;
  worksheetTemplateId?: string | undefined;
};

type Props = {
  automationActionBlock: AutomationActionBlock;
  locked: boolean;
};

const AutomationActionBlockForm = ({ automationActionBlock, locked }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [availableUserTypes, setAvailableUserTypes] = useState<AutomationUserTypeEnum[]>([]);

  const [requiredFields, setRequiredFields] = useState<AutomationActionBlockRequiredFieldEnum[]>([]);

  const getAutomationActionBlockRulesQuery = useQuery<
    GetAutomationActionBlockRulesQuery,
    GetAutomationActionBlockRulesQueryVariables
  >(GetAutomationActionBlockRulesDocument);

  const initialValues: FormData = {
    archiveTag: automationActionBlock.archiveTag ?? '',
    articleId: automationActionBlock.article?.id ?? '',
    carePlanTaskTemplateId: automationActionBlock.carePlanTaskTemplate?.id ?? '',
    duration: automationActionBlock.actionDuration,
    durationUnits: automationActionBlock.actionDurationUnits,
    goalTemplateId: automationActionBlock.goalTemplate?.id ?? '',
    kind: automationActionBlock.actionBlockKind ?? '',
    messageContent: automationActionBlock.messageContent ?? '',
    pushNotificationAction: automationActionBlock.pushNotificationAction,
    pushNotificationContent: automationActionBlock.pushNotificationContent ?? '',
    tag: automationActionBlock.tag ?? '',
    taskClientVisible: automationActionBlock.actionTaskClientVisible,
    taskDueDays: automationActionBlock.taskDueDays,
    taskDueHour: automationActionBlock.taskDueHour,
    taskKind: automationActionBlock.taskKind,
    taskTitle: automationActionBlock.taskTitle,
    userType: automationActionBlock.actionBlockUserType ?? '',
    worksheetTemplateId: automationActionBlock.worksheetTemplate?.id ?? ''
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      archiveTag: yup.string().nullable(),
      articleId: yup.string().nullable(),
      carePlanTaskTemplateId: yup.string(),
      duration: yup.number().nullable(),
      durationUnits: yup.string().nullable().oneOf(Object.values(DurationUnitsEnum)),
      goalTemplateId: yup.string(),
      kind: yup
        .string()
        .required(intl.formatMessage({ id: 'errors.automationActionBlock.kind.blank' }))
        .oneOf(
          Object.values(AutomationActionBlockKindEnum),
          intl.formatMessage({ id: 'errors.automationActionBlock.kind.inclusion' })
        ),
      messageContent: yup.string().nullable(),
      pushNotificationAction: yup.string().nullable().oneOf(Object.values(PushNotificationActionEnum)),
      pushNotificationContent: yup.string(),
      tag: yup.string().nullable(),
      taskClientVisible: yup.boolean().nullable(),
      taskDueDays: yup.number().nullable(),
      taskDueHour: yup.number().nullable(),
      taskKind: yup.string().nullable().oneOf(Object.values(TaskKindEnum)),
      taskTitle: yup.string().nullable(),
      userType: yup.string().oneOf(Object.values(AutomationUserTypeEnum)),
      worksheetTemplateId: yup.string()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const kind = form.watch('kind');

  useEffect(() => {
    const rule = getAutomationActionBlockRulesQuery.data?.automationActionBlockRules.find(rule => rule.kind === kind);
    if (rule) {
      setAvailableUserTypes(rule.userTypes ?? []);
      setRequiredFields(rule.requiredFields);
    } else {
      setAvailableUserTypes([]);
      setRequiredFields([]);
    }
  }, [getAutomationActionBlockRulesQuery.data?.automationActionBlockRules, kind]);

  const booleanOptions = optionsForBoolean(intl);
  const durationUnitsOptions = optionsForDurationUnits(intl);
  const hourOptions = optionsForHour(intl);
  const kindOptions = optionsForAutomationActionBlockKinds(intl);
  const pushNotificationActionOptions = optionsForPushNotificationAction(intl);
  const taskKindOptions = optionsForTaskKinds(intl);
  const userTypeOptions = optionsForAutomationUserType(intl, availableUserTypes);

  const [updateAutomationActionBlock] = useMutation<
    AutomationActionBlockUpdateMutation,
    AutomationActionBlockUpdateMutationVariables
  >(AutomationActionBlockUpdateDocument);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateAutomationActionBlock({
      variables: {
        ...form.getValues(),
        id: automationActionBlock.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.automationActionBlockUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.automationActionBlock'
      });
    });
  }, [updateAutomationActionBlock, form, automationActionBlock.id, intl]);

  const getCarePlanTaskTemplates = useLazyQueryWithPromise<
    GetCarePlanTaskTemplatesBasicQuery,
    GetCarePlanTaskTemplatesBasicQueryVariables
  >(GetCarePlanTaskTemplatesBasicDocument);

  const templateAsyncSearch = useAsyncSearch<CarePlanTaskTemplate>({
    defaultOptions: [],
    getLabel: t => t?.title ?? '',
    initialOption: {
      label: automationActionBlock?.carePlanTaskTemplate?.title,
      value: automationActionBlock?.carePlanTaskTemplate?.id
    }
  });

  return (
    <Form data-test="automation-action-block-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Select<FormData>
        control={form.control}
        disabled={locked}
        error={form.formState.errors.kind}
        label={intl.formatMessage({ id: 'forms.automationActionBlock.kindLabel' })}
        name="kind"
        options={kindOptions}
        required
      />
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.TAG)}
        fieldName="tag"
        form={form}
      >
        <TagSelect<FormData>
          control={form.control}
          disabled={locked}
          errors={form.formState.errors.tag}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.tagLabel' })}
          name="tag"
          required
          single
          tagType={TagTypeEnum.JOURNEY}
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.ARCHIVE_TAG)}
        fieldName="archiveTag"
        form={form}
      >
        <TagSelect<FormData>
          control={form.control}
          disabled={locked}
          errors={form.formState.errors.archiveTag}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.archiveTagLabel' })}
          name="archiveTag"
          required
          single
          tagType={TagTypeEnum.JOURNEY}
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.CARE_PLAN_TASK_TEMPLATE_ID)}
        fieldName="carePlanTaskTemplateId"
        form={form}
      >
        <AsyncSelect<FormData>
          control={form.control}
          defaultOptions={[]}
          disabled={locked}
          error={form.formState.errors.carePlanTaskTemplateId}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.carePlanTaskTemplateIdLabel' })}
          name="carePlanTaskTemplateId"
          onSearch={async searchTerm =>
            templateAsyncSearch.searchOptions(
              searchTerm,
              getCarePlanTaskTemplates({
                variables: {
                  count: 100,
                  searchTerm,
                  sortBy: CarePlanTaskTemplateSortByEnum.title
                }
              })
            )
          }
          onSelection={(option: Option) => {
            templateAsyncSearch.setCurrentOption(option);
          }}
          required
          value={templateAsyncSearch.currentOption}
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.MESSAGE_CONTENT)}
        fieldName="messageContent"
        form={form}
      >
        <RichTextArea<FormData>
          autofocus
          control={form.control}
          disabled={locked}
          error={form.formState.errors.messageContent}
          journeyId={null}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.messageContentLabel' })}
          name="messageContent"
          required
          sharableURLSWithoutJourneyId
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.PUSH_NOTIFICATION_CONTENT)}
        fieldName="pushNotificationContent"
        form={form}
      >
        <Input<FormData>
          autofocus
          control={form.control}
          disabled={locked}
          error={form.formState.errors.pushNotificationContent}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.pushNotificationContentLabel' })}
          name="pushNotificationContent"
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.PUSH_NOTIFICATION_ACTION)}
        fieldName="pushNotificationAction"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.pushNotificationAction}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.pushNotificationActionLabel' })}
          name="pushNotificationAction"
          options={pushNotificationActionOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.ARTICLE_ID)}
        fieldName="articleId"
        form={form}
      >
        <ArticleSelect<FormData>
          allowAny={false}
          control={form.control}
          disabled={locked}
          error={form.formState.errors.articleId}
          initialOption={{
            label: automationActionBlock.article?.title,
            value: automationActionBlock.article?.id ?? null
          }}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.articleIdLabel' })}
          name="articleId"
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.GOAL_TEMPLATE_ID)}
        fieldName="goalTemplateId"
        form={form}
      >
        <GoalSelect<FormData>
          allowAny={false}
          control={form.control}
          disabled={locked}
          error={form.formState.errors.goalTemplateId}
          initialOption={{
            label: automationActionBlock.goalTemplate?.title,
            value: automationActionBlock.goalTemplate?.id ?? null
          }}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.goalTemplateIdLabel' })}
          name="goalTemplateId"
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.DURATION)}
        fieldName="duration"
        form={form}
      >
        <Input<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.duration}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.durationLabel' })}
          name="duration"
          required
          type="number"
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.DURATION_UNITS)}
        fieldName="durationUnits"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.durationUnits}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.durationUnitsLabel' })}
          name="durationUnits"
          options={durationUnitsOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.TASK_DUE_DAYS)}
        fieldName="taskDueDays"
        form={form}
      >
        <Input<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.taskDueDays}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.taskDueDaysLabel' })}
          name="taskDueDays"
          required
          type="number"
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.TASK_DUE_HOUR)}
        fieldName="taskDueHour"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.taskDueHour}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.taskDueHourLabel' })}
          name="taskDueHour"
          options={hourOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.TASK_CLIENT_VISIBLE)}
        fieldName="taskClientVisible"
        form={form}
      >
        <BooleanRadioButtonGroup
          allowDeselection
          control={form.control}
          disabled={locked}
          error={form.formState.errors.taskClientVisible}
          inTracker={false}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.taskClientVisibleLabel' })}
          name="taskClientVisible"
          options={booleanOptions}
          small
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.TASK_KIND)}
        fieldName="taskKind"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.taskKind}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.taskKindLabel' })}
          name="taskKind"
          options={taskKindOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.TASK_TITLE)}
        fieldName="taskTitle"
        form={form}
      >
        <Input<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.taskTitle}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.taskTitleLabel' })}
          name="taskTitle"
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.WORKSHEET_TEMPLATE_ID)}
        fieldName="worksheetTemplateId"
        form={form}
      >
        <WorksheetTemplateSelect<FormData>
          allowAny={false}
          control={form.control}
          disabled={locked}
          error={form.formState.errors.articleId}
          initialOption={{
            label: automationActionBlock?.worksheetTemplate?.title,
            value: automationActionBlock.worksheetTemplate?.id ?? null
          }}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.worksheetTemplateIdLabel' })}
          name="worksheetTemplateId"
          required
        />
      </FormCondition>

      <FormCondition
        condition={requiredFields.includes(AutomationActionBlockRequiredFieldEnum.USER_TYPE)}
        fieldName="userType"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.userType}
          label={intl.formatMessage({ id: 'forms.automationActionBlock.userTypeLabel' })}
          name="userType"
          options={userTypeOptions}
          required
        />
      </FormCondition>

      {!locked && <FormButtons hookForm={form} skipCancel />}
    </Form>
  );
};

export default AutomationActionBlockForm;
